import { Typography, Row, Col, Modal, Table }  from 'antd'
import { useState } from 'react'
import Breakpoint from "../../enums/breakpoint.enum";
import useWidth from '../../hooks/useWidth.hook';

const { Text, Paragraph } = Typography

const ToxicMetalsModal = ({ isOpen, onClose }) => {
  const columns = [
    {
      title: 'Metal',
      dataIndex: 'metal',
      key: 'metal',
    },
    {
      title: 'Effect',
      dataIndex: 'effect',
      key: 'effect',
    }
  ];

  const data = [
    { key: '1', metal: 'Aluminum', effect: 'Neurotoxic metal associated with memory loss and impaired cognition, especially in renal dysfunction or high supplement use' },
    { key: '2', metal: 'Antimony', effect: 'Industrial metalloid linked to lung irritation, heart rhythm abnormalities, and chronic occupational exposure risk' },
    { key: '3', metal: 'Arsenic', effect: 'Environmental toxin found in water, rice, and seafood, associated with skin lesions, cardiovascular disease, and cancer' },
    { key: '4', metal: 'Barium', effect: 'Heavy metal that disrupts muscle function and cardiac conduction, primarily from contaminated water or occupational sources' },
    { key: '5', metal: 'Beryllium', effect: 'Lightweight industrial metal that can cause chronic beryllium lung disease and immune sensitization in exposed workers' },
    { key: '6', metal: 'Bismuth', effect: 'Pharmaceutical metal used in antacids and antiseptics, linked to reversible encephalopathy at high doses' },
    { key: '7', metal: 'Cadmium', effect: 'Highly toxic metal from smoking and industrial waste, causing kidney damage, bone demineralization, and reproductive effects' },
    { key: '8', metal: 'Cesium', effect: 'Radioactive or industrial metal with potential cardiovascular, metabolic, and nervous system effects' },
    { key: '9', metal: 'Gadolinium', effect: 'MRI contrast agent retained in tissues in some patients, especially those with kidney disease, raising concerns about fibrosis' },
    { key: '10', metal: 'Lead', effect: 'Potent neurotoxin affecting brain development in children and causing anemia, hypertension, and cognitive decline in adults' },
    { key: '11', metal: 'Mercury', effect: 'Neurotoxic metal from fish, dental fillings, and vapors, linked to tremors, memory loss, and kidney dysfunction' },
    { key: '12', metal: 'Nickel', effect: 'Common contact allergen causing eczema, and a sensitizer linked to occupational asthma and systemic nickel allergy syndrome' },
    { key: '13', metal: 'Palladium', effect: 'Precious metal used in dentistry and electronics, associated with metal hypersensitivity and contact reactions' },
    { key: '14', metal: 'Platinum', effect: 'Metal used in chemotherapy and catalysts, associated with kidney toxicity, allergic reactions, and hearing loss' },
    { key: '15', metal: 'Tellurium', effect: 'Rare industrial element that causes garlic-like breath odor, fatigue, and nerve toxicity at high exposures' },
    { key: '16', metal: 'Thallium', effect: 'Extremely toxic metal that interferes with nerve and heart function, often from contaminated food or poisoning' },
    { key: '17', metal: 'Thorium', effect: 'Naturally radioactive metal used in nuclear and aerospace industries, linked to internal radiation exposure and cancer risk' },
    { key: '18', metal: 'Tin', effect: 'Metal used in cans and plastics; inorganic forms may cause GI upset and neurological symptoms at high levels' },
    { key: '19', metal: 'Tungsten', effect: 'Hard metal found in military and machining applications, associated with lung inflammation and immune dysfunction' },
    { key: '20', metal: 'Uranium', effect: 'Radioactive metal that targets the kidneys and may cause DNA damage with chronic environmental exposure' }
  ]

  return (
    <Modal
      title="20 Toxic Metals Tested + Their Effects"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      <Table 
        columns={columns} 
        dataSource={data} 
        pagination={false}
        className="toxic-metals-table"
      />
    </Modal>
  );
};

export const ToxicMetalsTestIntro = ({ footer, productType }) => {
  const width = useWidth()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const instructions = [
    {
      sections: [
        {
        title: 'Select Collection Method',
        description: <>You can choose to collect your urine, hair or both.</>
      }
    ],
  },
  {
    sections: [
      {
        title: 'Get Your Collection Kit',
        description: <>It'll be delivered within one week of purchase. This includes everything you'll need to collect your urine or hair for testing.</>
      }
    ],
  },
  { 
    sections: [
      {
        title: 'Self Collect Urine or Hair + Mail Back',
        description: <>You'll collect your hair or urine over a 24 hour period. Just drop in mail when done.</>
      }
    ],
  },
  { 
    sections: [
      {
        title: 'View Results Online',
        description: <>Test results are usually ready 1 week after your sample arrives at the lab.</>
      }
      ]
    }
  ]

  return (
    <div 
      className="flow-intro"
      style={{
        minHeight: width >= Breakpoint.XL ? 'calc(100vh - 86px)' : 'calc(100vh - 60px)',
      }}
    >

    <ToxicMetalsModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />


      <div 
        className="flow-intro-body"
        style={{
          minHeight: width >= Breakpoint.XL ? 'calc(100vh - 86px)' : 'calc(100vh - 60px)',
        }}
      >
        <Paragraph 
          className="flow-title"
          style={{
            fontSize: width > Breakpoint.SM ? 32 : 28,
          }}
        >
           Heavy Metals Test
        </Paragraph>


        <Paragraph 
          className="flow-description"
        >
          Detect your exposure to <a href="#" onClick={() => setIsModalOpen(true)}>20 toxic metals</a>, including heavy metals like mercury, arsenic, and lead. 
          
    
        

        </Paragraph>

        <div 
          className="instruction-list"
          style={{
            marginTop: width > Breakpoint.SM ? 50 : 30,
            marginBottom: width > Breakpoint.SM ? -15 : -25,
            paddingTop: width > Breakpoint.SM ? 25 : 15,
          }}
        >
          {instructions.map(({ sections }, instructionIndex) => (
            <div 
              key={`instruction-${instructionIndex}`} 
              className="instruction-item"
            >
              <Row gutter={10}>
                <Col
                  xs={{ span: 5 }}
                  sm={{ span: 4 }}
                  md={{ span: 3 }}
                  lg={{ span: 3 }}
                  xl={{ span: 3 }}
                  xxl={{ span: 3 }}
                >
                  <Text className="instruction-index">
                    Step {instructionIndex+1}
                  </Text>
                </Col>

                <Col
                  xs={{ span: 19 }}
                  sm={{ span: 20 }}
                  md={{ span: 21 }}
                  lg={{ span: 21 }}
                  xl={{ span: 21 }}
                  xxl={{ span: 21 }}
                >
                  {sections.map(({ title, description }, sectionIndex) => (
                    <div 
                      key={`section-${instructionIndex}-${sectionIndex}`} 
                      className="section-item"
                    >
                      <Text className="section-title">
                        {title}
                      </Text>
                      <Paragraph className="section-description">
                        {description}
                      </Paragraph>
                    </div>
                  ))}
                  </Col>
              </Row>
            </div>
          ))}
        </div>

        <div className="flow-intro-btn-container">
          <div className="flow-intro-btn">
            {footer({ 
              buttonText: "Let's go",
              buttonCheck: false,
              buttonStyle: {
                height: 50,
                fontSize: 18,
                width: '100%',
                display: 'block',
              },
              enterStyle: {
                display: 'none',
              }
            })}
          </div>
        </div>
      </div>
    </div>
  )
}

